import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, User, UserPlus, LogIn, Loader2, ShieldCheck, Phone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { useUser } from "../../context/UserContext";
import { forgotPassword, formatUSPhone } from "../../lib/api";

function Field({ icon: Icon, ...props }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <Input className="pl-10" {...props} />
    </div>
  );
}

export default function AuthModal({ open, onOpenChange, defaultTab = "signup" }) {
  const { login, signup } = useUser();
  const navigate = useNavigate();
  const [tab, setTab] = useState(defaultTab);
  const [loading, setLoading] = useState(false);
  const [signIn, setSignIn] = useState({ email: "", password: "" });
  const [signUp, setSignUp] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });
  const [forgot, setForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const handleForgot = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error("Please enter your email address.");
      return;
    }
    setLoading(true);
    try {
      const res = await forgotPassword(forgotEmail);
      setForgotSent(true);
      setDevLink(res.reset_link || "");
      toast.success("If an account exists, a reset link is on its way.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const closeForgot = () => {
    setForgot(false);
    setForgotSent(false);
    setForgotEmail("");
    setDevLink("");
  };

  useEffect(() => {
    if (open) setTab(defaultTab);
  }, [defaultTab, open]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    if (!signIn.email || !signIn.password) {
      toast.error("Please enter your email and password.");
      return;
    }
    setLoading(true);
    const res = await login(signIn);
    setLoading(false);
    if (res.ok) {
      if (res.admin) {
        // Send staff to the back-office CRM instead of the client portal.
        localStorage.setItem("rfl_crm_token", res.token);
        toast.success("Welcome back! Opening your back office…");
        onOpenChange(false);
        setSignIn({ email: "", password: "" });
        window.location.href = "/crm";
        return;
      }
      toast.success("Welcome back!");
      onOpenChange(false);
      setSignIn({ email: "", password: "" });
      navigate("/portal");
    } else {
      toast.error(res.error);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (!signUp.name || !signUp.email || !signUp.password) {
      toast.error("Please fill in all fields.");
      return;
    }
    if (signUp.password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (signUp.password !== signUp.confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setLoading(true);
    const res = await signup(signUp);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created — you're now signed in!");
      onOpenChange(false);
      setSignUp({ name: "", email: "", phone: "", password: "", confirm: "" });
      navigate("/portal");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden">
        <div className="bg-navy px-6 pt-7 pb-6 text-center">
          <div className="mx-auto h-12 w-12 rounded-full bg-gold flex items-center justify-center font-serif font-bold text-navy text-lg">
            RFL
          </div>
          <DialogHeader className="mt-3 space-y-1">
            <DialogTitle className="font-serif text-white text-xl text-center">
              Welcome to Rehabilitated For Life SSM
            </DialogTitle>
            <DialogDescription className="text-gray-300 text-center">
              Create your free account to begin your journey to financial freedom.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="px-6 pb-6 pt-5">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 w-full mb-5">
              <TabsTrigger value="signup">Create Account</TabsTrigger>
              <TabsTrigger value="signin">Sign In</TabsTrigger>
            </TabsList>

            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-3">
                <Field
                  icon={User}
                  placeholder="Full name"
                  value={signUp.name}
                  onChange={(e) => setSignUp({ ...signUp, name: e.target.value })}
                />
                <Field
                  icon={Mail}
                  type="email"
                  placeholder="Email address"
                  value={signUp.email}
                  onChange={(e) => setSignUp({ ...signUp, email: e.target.value })}
                />
                <Field
                  icon={Phone}
                  type="tel"
                  placeholder="Phone number — (555) 123-4567"
                  value={signUp.phone}
                  onChange={(e) => setSignUp({ ...signUp, phone: formatUSPhone(e.target.value) })}
                  data-testid="signup-phone-input"
                />
                <Field
                  icon={Lock}
                  type="password"
                  placeholder="Password (min. 6 characters)"
                  value={signUp.password}
                  onChange={(e) => setSignUp({ ...signUp, password: e.target.value })}
                />
                <Field
                  icon={Lock}
                  type="password"
                  placeholder="Confirm password"
                  value={signUp.confirm}
                  onChange={(e) => setSignUp({ ...signUp, confirm: e.target.value })}
                />
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold h-11"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <UserPlus className="h-4 w-4 mr-1.5" /> Create Free Account
                    </>
                  )}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="signin">
              {forgot ? (
                forgotSent ? (
                  <div className="space-y-3 text-center py-2" data-testid="forgot-sent">
                    <p className="text-sm text-gray-600">
                      If an account exists for <span className="font-medium text-navy">{forgotEmail}</span>, you'll receive an email with a link to reset your password.
                    </p>
                    {devLink && (
                      <div className="bg-cream rounded-lg p-3 text-xs text-gray-600 break-all">
                        <span className="font-medium text-navy">Dev link (email not configured): </span>
                        <a href={devLink} className="text-gold-dark underline" data-testid="forgot-dev-link">{devLink}</a>
                      </div>
                    )}
                    <button type="button" onClick={closeForgot} className="text-sm text-navy hover:underline" data-testid="forgot-back-button">
                      Back to sign in
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleForgot} className="space-y-3" data-testid="forgot-form">
                    <p className="text-sm text-gray-500">Enter your email and we'll send you a link to reset your password.</p>
                    <Field
                      icon={Mail}
                      type="email"
                      placeholder="Email address"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      data-testid="forgot-email-input"
                    />
                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full bg-navy hover:bg-navy-light text-white font-semibold h-11"
                      data-testid="forgot-submit-button"
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Reset Link"}
                    </Button>
                    <button type="button" onClick={closeForgot} className="w-full text-center text-sm text-gray-500 hover:text-navy">
                      Back to sign in
                    </button>
                  </form>
                )
              ) : (
              <form onSubmit={handleSignIn} className="space-y-3">
                <Field
                  icon={Mail}
                  type="email"
                  placeholder="Email address"
                  value={signIn.email}
                  onChange={(e) => setSignIn({ ...signIn, email: e.target.value })}
                />
                <Field
                  icon={Lock}
                  type="password"
                  placeholder="Password"
                  value={signIn.password}
                  onChange={(e) => setSignIn({ ...signIn, password: e.target.value })}
                />
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => setForgot(true)}
                    className="text-xs text-gold-dark hover:underline"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-navy hover:bg-navy-light text-white font-semibold h-11"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <LogIn className="h-4 w-4 mr-1.5" /> Sign In
                    </>
                  )}
                </Button>
              </form>
              )}
            </TabsContent>
          </Tabs>

          <div className="mt-5 flex items-center justify-center gap-1.5 text-xs text-gray-400">
            <ShieldCheck className="h-3.5 w-3.5" /> Your information is kept private and secure.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
